import { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, X } from "lucide-react";
import api, { BACKEND_URL, formatApiErrorDetail } from "../../lib/api";
import { logger } from "../../lib/logger";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { MasterEditor } from "./MasterEditor";

const resolveImg = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

/** Generic sortable card used by both artworks + artists lists. */
function SortableCard({ id, kind, item, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`featured-card-${kind}-${id}`}
      className="border border-[#E5E0D8] bg-white flex items-center gap-3 p-2"
    >
      <button
        {...attributes}
        {...listeners}
        aria-label="Drag"
        className="cursor-grab active:cursor-grabbing text-[#555] hover:text-[#111] p-1 flex-shrink-0"
      >
        <GripVertical className="w-4 h-4" strokeWidth={1.5} />
      </button>
      <img
        src={resolveImg(item.image_url || item.avatar_url) || "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=120"}
        alt={item.title || item.name}
        className="w-14 h-14 object-cover bg-[#F4EFE6] flex-shrink-0"
        onError={(e) => {
          e.currentTarget.src = "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=120";
        }}
      />
      <div className="flex-1 min-w-0">
        <p className="font-serif-display text-sm text-[#111] truncate">
          {item.title || item.name}
        </p>
        <p className="font-mono-aam text-xs text-[#555] mt-0.5 truncate">
          {item.title ? `${item.artist_name} · $${(item.price || 0).toLocaleString()}` : item.country}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onRemove(id)}
        aria-label="Remove from featured"
        data-testid={`unfeature-${kind}-${id}`}
        className="text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white p-2 transition-colors flex-shrink-0"
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}

/** Reusable column with a sortable list of pinned items + a picker below for adding. */
function CuratedColumn({ kind, label, helper, max, pinnedIds, byId, candidates, search, setSearch, onReorder, onAdd, onRemove, renderItemFooter }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const pinnedItems = useMemo(
    () => pinnedIds.map((id) => byId[id]).filter(Boolean),
    [pinnedIds, byId]
  );
  const handleDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = pinnedIds.indexOf(active.id);
    const newIndex = pinnedIds.indexOf(over.id);
    onReorder(arrayMove(pinnedIds, oldIndex, newIndex));
  };
  const lowerSearch = (search || "").toLowerCase();
  const filteredCandidates = candidates
    .filter((c) => !pinnedIds.includes(c.id))
    .filter((c) =>
      !lowerSearch ||
      (c.title || c.name || "").toLowerCase().includes(lowerSearch) ||
      (c.artist_name || c.country || "").toLowerCase().includes(lowerSearch)
    )
    .slice(0, 30);

  return (
    <div className="border border-[#E5E0D8] bg-[#F4EFE6]/30 p-5">
      <p className="label-eyebrow text-[#1E3A8A]">— {label}</p>
      <h3 className="font-serif-display text-2xl mt-2 text-[#111]">
        {pinnedItems.length} pinned <span className="font-mono-aam text-xs text-[#555]">/ filled to {max} on home</span>
      </h3>
      <p className="text-xs text-[#555] mt-2">{helper}</p>

      <div className="mt-5">
        {pinnedItems.length === 0 ? (
          <p className="label-eyebrow text-[#555] py-6 text-center border border-dashed border-[#E5E0D8]">
            Nothing pinned yet — homepage shows the latest below.
          </p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={pinnedIds} strategy={rectSortingStrategy}>
              <div data-testid={`pinned-list-${kind}`} className="space-y-2">
                {pinnedItems.map((it) => (
                  <div key={it.id}>
                    <SortableCard id={it.id} kind={kind} item={it} onRemove={onRemove} />
                    {renderItemFooter?.(it)}
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <div className="hairline-divider my-6" />

      <p className="label-eyebrow text-[#555]">— Add to featured</p>
      <Input
        data-testid={`featured-search-${kind}`}
        placeholder={`Search ${kind === "artwork" ? "artworks" : "artists"} by name…`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mt-3 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-3 focus-visible:ring-0 focus-visible:border-[#111]"
      />
      <div className="mt-3 max-h-[260px] overflow-y-auto border border-[#E5E0D8] bg-white">
        {filteredCandidates.length === 0 ? (
          <p className="p-4 label-eyebrow text-[#555]">No matches.</p>
        ) : (
          filteredCandidates.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`feature-add-${kind}-${c.id}`}
              onClick={() => onAdd(c.id)}
              disabled={pinnedItems.length >= max}
              className="w-full flex items-center gap-3 p-2 border-b border-[#E5E0D8] last:border-b-0 hover:bg-[#F4EFE6]/40 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <img
                src={resolveImg(c.image_url || c.avatar_url) || "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=120"}
                alt=""
                className="w-10 h-10 object-cover bg-[#F4EFE6] flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.src = "https://images.unsplash.com/photo-1500336624523-d727130c3328?w=120";
                }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-serif-display text-sm text-[#111] truncate">{c.title || c.name}</p>
                <p className="font-mono-aam text-xs text-[#555] mt-0.5 truncate">
                  {c.title ? `${c.artist_name} · $${(c.price || 0).toLocaleString()}` : c.country}
                </p>
              </div>
              <Plus className="w-4 h-4 text-[#1E3A8A] flex-shrink-0" strokeWidth={1.5} />
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export default function AdminFeatured() {
  const [artworks, setArtworks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [pinnedArtworks, setPinnedArtworks] = useState([]);
  const [pinnedArtists, setPinnedArtists] = useState([]);
  const [mastersMeta, setMastersMeta] = useState({});
  const [openEditorId, setOpenEditorId] = useState(null);
  const [searchArt, setSearchArt] = useState("");
  const [searchArtist, setSearchArtist] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch everything we need
  useEffect(() => {
    (async () => {
      try {
        const [aw, ar, settings] = await Promise.all([
          api.get("/admin/artworks?limit=200"),
          api.get("/artists"),
          api.get("/admin/settings"),
        ]);
        setArtworks((aw.data?.items || []).filter((a) => a.status !== "draft" && !a.sold));
        setArtists(ar.data || []);
        setPinnedArtworks(settings.data?.featured_artwork_ids || []);
        setPinnedArtists(settings.data?.featured_artist_ids || []);
        setMastersMeta(settings.data?.masters_meta || {});
      } catch (err) {
        logger.error("AdminFeatured load failed:", err);
        toast.error("Could not load featured curation data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const artworksById = useMemo(
    () => Object.fromEntries(artworks.map((a) => [a.id, a])),
    [artworks]
  );
  const artistsById = useMemo(
    () => Object.fromEntries(artists.map((a) => [a.id, a])),
    [artists]
  );

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/featured", {
        artwork_ids: pinnedArtworks,
        artist_ids: pinnedArtists,
      });
      setPinnedArtworks(data.featured_artwork_ids || []);
      setPinnedArtists(data.featured_artist_ids || []);
      // Surface server-side auto-since stamping for new pins
      if (data.masters_meta) setMastersMeta(data.masters_meta);
      toast.success("Homepage order saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear ALL homepage curation? The homepage will fall back to the latest works.")) return;
    setSaving(true);
    try {
      await api.patch("/admin/featured", { artwork_ids: [], artist_ids: [] });
      setPinnedArtworks([]);
      setPinnedArtists([]);
      toast.success("Cleared");
    } catch (err) {
      toast.error("Clear failed");
    } finally {
      setSaving(false);
    }
  };

  const updateMasterMeta = (artistId, meta) => {
    setMastersMeta((prev) => {
      const next = { ...prev };
      if (meta && (meta.blurb || meta.spotlight_url || meta.since)) {
        next[artistId] = meta;
      } else {
        delete next[artistId];
      }
      return next;
    });
  };

  if (loading) return <div className="label-eyebrow text-[#555]">Loading curation…</div>;

  return (
    <div data-testid="admin-featured-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-[#1E3A8A]">— Homepage curation</p>
          <h1 className="font-serif-display text-5xl tracking-tighter font-medium mt-3 text-[#111]">
            Masters in Residence
          </h1>
          <p className="text-[#555] mt-3 max-w-2xl">
            Drag to reorder. Empty slots auto-fill with the latest works / newest artists so the
            homepage is never empty. Add editorial blurbs + a spotlight photo per master to give
            each card a curated voice.
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button
            onClick={clearAll}
            disabled={saving || (pinnedArtworks.length === 0 && pinnedArtists.length === 0)}
            data-testid="featured-clear-all"
            variant="outline"
            className="rounded-none border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white label-eyebrow px-5 py-3 h-auto"
          >
            Clear all
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="featured-save-btn"
            className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-8 py-3 h-auto label-eyebrow"
          >
            {saving ? "Saving…" : "Save order"}
          </Button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CuratedColumn
          kind="artwork"
          label="Featured Artworks"
          helper="Shown in the 'Featured Art' grid on the homepage."
          max={6}
          pinnedIds={pinnedArtworks}
          byId={artworksById}
          candidates={artworks}
          search={searchArt}
          setSearch={setSearchArt}
          onReorder={setPinnedArtworks}
          onAdd={(id) =>
            setPinnedArtworks((prev) => (prev.includes(id) ? prev : [...prev, id]))
          }
          onRemove={(id) => setPinnedArtworks((prev) => prev.filter((x) => x !== id))}
        />
        <CuratedColumn
          kind="artist"
          label="Masters in Residence"
          helper="Shown in the 'Masters in residence' grid on the homepage. Tap each card to add an editorial blurb and spotlight photo."
          max={8}
          pinnedIds={pinnedArtists}
          byId={artistsById}
          candidates={artists}
          search={searchArtist}
          setSearch={setSearchArtist}
          onReorder={setPinnedArtists}
          onAdd={(id) =>
            setPinnedArtists((prev) => (prev.includes(id) ? prev : [...prev, id]))
          }
          onRemove={(id) => {
            setPinnedArtists((prev) => prev.filter((x) => x !== id));
            if (openEditorId === id) setOpenEditorId(null);
          }}
          renderItemFooter={(artist) => (
            <MasterEditor
              artist={artist}
              masterMeta={mastersMeta[artist.id]}
              open={openEditorId === artist.id}
              onToggle={() => setOpenEditorId((cur) => (cur === artist.id ? null : artist.id))}
              onSaved={updateMasterMeta}
            />
          )}
        />
      </div>
    </div>
  );
}
